import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiActions,
  MiBox,
  MiButton,
  MiDetailFields,
  MiMenu,
  MiModalConfirm,
  MiPlaceholderList,
  MiSectionHeader,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  MODAL_TITLES,
} from "@miplatform/constants";
import { sortByOptionStatus } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import * as mdi from "@mdi/js";
import { Box, GridLegacy, Typography, useTheme } from "@mui/material";
import { OptionCard } from "../OptionCard/OptionCard";
import { OptionAreaPriceSummary } from "../OptionPricing/OptionAreaPriceSummary";
import { OptionSummaryMarkupForm } from "../OptionPricing/OptionSummaryMarkupForm";
import { OptionDetails } from "./OptionDetails/OptionDetails";

const schema = z.object({
  name: z.string().min(1, "Option name is required"),
});

export function OptionsSection({
  optionDetailsMap,
  setOptionDetailsMap,
  getOption,
  stateManager,
  priceRollup,
  optionData,
  setOptionData,
  selectedOption,
  setSelectedOption,
  showOptionPreviews,
  setShowOptionPreviews,
  showOptionSummary,
  setShowOptionSummary,
  getPreviewCompatibility,
  optionPriceSummaryMap,
  getAllOptionPriceSummaries,
  handleCreateOption,
  handleUpdateOption,
  handleDeleteOption,
  handleCloseAreaDrawer,
  handleCloseCategoryFilter,
}) {
  const {
    formState: { isValid },
    control,
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: { name: selectedOption?.name || "" },
    resolver: zodResolver(schema),
  });
  const { classes } = useStyles();
  const theme = useTheme();
  const { specPlanId } = useParams();

  const [openOptionNameForm, setOpenOptionNameForm] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const isLoading = stateManager.isBusy();
  const optionIds = Object.keys(optionDetailsMap);

  useEffect(() => {
    getAllOptionPriceSummaries(specPlanId);
  }, [JSON.stringify(optionDetailsMap), optionIds.length]);

  const optionNameField = [
    {
      name: "name",
      label: "Name",
      placeholder: "Enter new option name",
      formControl: control,
      padding: theme.spacing(1),
      inputProps: { maxLength: 64 },
    },
  ];

  const onBack = () => {
    handleCloseCategoryFilter();
    handleCloseAreaDrawer();
    setSelectedOption(null);
    setOptionData({});
    reset({ name: "" });
  };

  const handleOpenDeleteModal = (option) => {
    setOpenDeleteModal(true);
    setSelectedOption(option);
  };

  const handleConfirmDeleteModal = () => {
    handleDeleteOption(selectedOption);
    setOpenDeleteModal(false);
  };

  const handleSetShowPreview = useCallback(
    (id, value) => {
      setOptionDetailsMap((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          showPreview: value,
        },
      }));
    },
    [optionDetailsMap]
  );

  const renderActions = useCallback(
    (option) => (
      <Box
        onClick={(e) => e.stopPropagation()}
        display="flex"
        alignItems="center"
      >
        <MiMenu
          disabled={isLoading || showOptionPreviews}
          data={[
            {
              label: BUTTON_LABELS.EDIT,
              icon: mdi[ICONS_WEB.PENCIL],
              action: () => {
                setSelectedOption(option);
                reset({ name: option.name });
                setOpenOptionNameForm(true);
              },
            },
            {
              label: BUTTON_LABELS.DELETE,
              icon: mdi[ICONS_WEB.TRASH_CAN],
              action: () => handleOpenDeleteModal(option),
            },
          ]}
        />
      </Box>
    ),
    [selectedOption, showOptionPreviews, isLoading, openOptionNameForm]
  );

  const renderPriceSummary = useCallback(
    (optionId) => {
      const optionPriceSummary = optionPriceSummaryMap[optionId];
      const itemGridLegacyWidth =
        12 - (optionPriceSummary?.draws?.length || 0) * 2;

      return (
        <GridLegacy
          container
          sx={{ flexGrow: 1 }}
          spacing={1}
          padding={theme.spacing(0, 8, 2, 1)}
        >
          <GridLegacy item xs={itemGridLegacyWidth} />
          {optionPriceSummary?.draws?.map((draw) => (
            <GridLegacy
              item
              xs={2}
              display="flex"
              justifyContent="flex-end"
              key={`${draw.draw}-${optionId}-priceHeader`}
            >
              <Typography variant="body2" fontWeight={theme.fontWeight.bold}>
                {draw.draw}
              </Typography>
            </GridLegacy>
          ))}
          {sortByOptionStatus(optionPriceSummary?.planAreas).map((area, i) => {
            return (
              <OptionAreaPriceSummary
                area={area}
                index={i}
                optionId={optionId}
                key={`${area.id}-${optionId}-priceSummary`}
              />
            );
          })}
        </GridLegacy>
      );
    },
    [optionDetailsMap, priceRollup, isLoading]
  );

  const renderOptionsList = () => {
    return (
      <>
        <MiSectionHeader
          title="Options"
          rightAction={
            <Box display="flex" gap={2}>
              <MiButton
                title={
                  !showOptionPreviews ? "Preview Option(s)" : "Exit Preview"
                }
                variant={BUTTON_VARIANTS.TEXT}
                onClick={() => {
                  setShowOptionPreviews(!showOptionPreviews);
                }}
                disabled={isLoading || optionIds.length === 0}
              />
              <MiButton
                title="Option"
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                onClick={() => {
                  reset({ name: "" });
                  setOpenOptionNameForm(true);
                  setShowOptionPreviews(false);
                }}
                disabled={isLoading}
              />
            </Box>
          }
        />
        {optionIds.length === 0 ? (
          <MiPlaceholderList
            text="No options added."
            helperText="To get started with a new Option, click the + Option button above"
          />
        ) : (
          <Box className={classes.overflowContainer}>
            {optionIds?.map((optionId) => (
              <OptionCard
                key={optionId}
                option={optionDetailsMap[optionId]}
                priceSummary={optionPriceSummaryMap[optionId] ?? {}}
                renderActions={() => renderActions(optionDetailsMap[optionId])}
                renderPriceSummary={() => renderPriceSummary(optionId)}
                showOptionPreviews={showOptionPreviews}
                handleSetShowPreview={handleSetShowPreview}
                previewUnavailable={getPreviewCompatibility(
                  optionDetailsMap[optionId]
                )}
              />
            ))}
          </Box>
        )}
      </>
    );
  };

  const renderOptionNameForm = () => {
    const title = selectedOption ? "Edit Option" : "Create Option";
    return (
      <>
        <MiSectionHeader title={title} />
        <MiDetailFields detailFields={optionNameField} />
        <Box className={classes.actionsContainer}>
          <MiActions
            actions={[
              {
                name: "Cancel",
                onClick: () => {
                  setOpenOptionNameForm(false);
                  setSelectedOption(null);
                  reset({ name: "" });
                },
                variant: BUTTON_VARIANTS.OUTLINED,
              },
              {
                name: "Continue",
                onClick: async () => {
                  if (!selectedOption) {
                    const response = await handleCreateOption(getValues());
                    if (response?.data) {
                      setOpenOptionNameForm(false);
                    }
                  } else {
                    // continue with update, fetching/setting optionData and replacing option name if edited by user
                    await getOption(selectedOption);
                    setOpenOptionNameForm(false);
                    setSelectedOption({
                      ...selectedOption,
                      name: getValues("name"),
                    });
                  }
                },
                variant: BUTTON_VARIANTS.CONTAINED,
                disabled: !isValid || isLoading,
              },
            ]}
          />
        </Box>
      </>
    );
  };

  return (
    <MiBox className={classes.container}>
      {!optionData?.id && (
        <>
          {!openOptionNameForm && renderOptionsList()}
          {openOptionNameForm && renderOptionNameForm()}
        </>
      )}
      {optionData?.id && (
        <Box margin={0} display="flex" flexDirection="column" flex={1}>
          {!showOptionSummary ? (
            <OptionDetails
              selectedOption={selectedOption}
              isLoading={isLoading}
              onSave={() => handleUpdateOption(getValues("name"))}
              onBack={onBack}
              setShowOptionSummary={setShowOptionSummary}
            />
          ) : (
            <OptionSummaryMarkupForm
              optionDetails={optionData}
              renderPriceSummary={() => renderPriceSummary(optionData?.id)}
              priceRollup={priceRollup}
              isLoading={isLoading}
              onBack={() => setShowOptionSummary(false)}
            />
          )}
        </Box>
      )}
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.red.main,
        }}
        handleNegative={() => {
          setOpenDeleteModal(false);
          setSelectedOption(null);
        }}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedOption?.name}. Are you sure you would like to continue?`}
      />
    </MiBox>
  );
}

const useStyles = makeStyles()(() => ({
  container: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  actionsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  overflowContainer: {
    overflow: "auto",
    height: "100%",
  },
}));
